import { addDays, isAfter, isValid, parseISO } from 'date-fns';
import {
  CONTESTATION_WINDOW_DAYS,
  type ReleaseStatusLike,
} from './types';

/**
 * Prazo de contestação da medição — dias corridos a partir da publicação do ciclo (RFC RN-01).
 * Não confundir com a janela de retenção (`release_at`), que é em dias úteis e diz quando a
 * medição segue para faturamento.
 */
export function contestationDeadline(publishedAt: string | null): Date | null {
  if (!publishedAt) return null;
  const published = parseISO(publishedAt);
  if (!isValid(published)) return null;
  return addDays(published, CONTESTATION_WINDOW_DAYS);
}

/**
 * Um prazo de contestação é um prazo **brasileiro**, e é no calendário de Brasília que ele tem
 * de ser lido.
 *
 * O backend grava `release_at` como 23:59:59 de Brasília (`end_of_business_day` do
 * `integracao-faturamento`) — um segundo antes da meia-noite. Formatar esse instante no fuso do
 * navegador mostra **o dia seguinte** em qualquer fuso a leste de -03:00, UTC incluído: a CI,
 * que roda em UTC, leu 10/09 onde o prazo é 09/09. Num navegador brasileiro não muda nada; fora
 * disso, deixava de anunciar a data certa, que é justamente o que este módulo existe para fazer.
 */
const DEADLINE_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatDeadline(deadline: Date): string {
  return DEADLINE_FORMATTER.format(deadline);
}

/**
 * A data que a tela mostra: o fim da janela de retenção — `published_at + retention_days` em dias
 * úteis, calculado pelo backend e entregue em `release_at`.
 *
 * **É a única data exibida** (decisão de produto de 08/09/2026). O teto dos 120 dias corridos da
 * RN-01 continua valendo como regra — `canContest` o aplica, e é ele que recusa contestação fora
 * do prazo — mas não aparece mais no cabeçalho: anunciar dois prazos diferentes para a mesma
 * medição era o que confundia quem lia a tela.
 *
 * Sem data quando:
 *
 * - **já é fatura** — ali o prazo que existe é o dos 120 dias, e ele não é para exibir;
 * - **sem `release_at`** — `retention_days = 0` ou ciclo antigo: não há janela de retenção, e
 *   cair no teto seria mostrar justamente o prazo que saiu de tela;
 * - **ciclo cancelado ou sem estado.**
 *
 * A data vai como o backend calculou, sem nenhum ajuste aqui: o dia da publicação conta como 1º
 * dia útil (`add_business_days`) e o prazo vence no fim do dia (`end_of_business_day`).
 */
export function retentionWindowDeadline(
  window: Pick<ContestationWindow, 'releaseStatus' | 'releaseAt' | 'isInvoiced'>,
): Date | null {
  const { releaseStatus, releaseAt, isInvoiced } = window;

  if (releaseStatus == null || releaseStatus === 'CANCELLED') return null;
  if (isInvoiced) return null;
  if (!releaseAt) return null;

  const closesAt = parseISO(releaseAt);
  return isValid(closesAt) ? closesAt : null;
}

/** A janela de retenção já venceu. Sem `release_at` não há como afirmar que venceu. */
function retentionWindowClosed(releaseAt: string | null | undefined, now: Date): boolean {
  if (!releaseAt) return false;
  const closesAt = parseISO(releaseAt);
  if (!isValid(closesAt)) return false;
  return isAfter(now, closesAt);
}

export interface ContestationWindow {
  releaseStatus: ReleaseStatusLike;
  publishedAt: string | null;
  /** Fim da janela de retenção — o prazo do `retention_days` (RN-21). */
  releaseAt?: string | null;
  /** A medição virou fatura: existe OS na Omie. */
  isInvoiced?: boolean;
  /**
   * Alguma contestação desta medição já foi decidida (aceita, rejeitada ou com correção
   * aplicada). Inclui as das coletas: aceitar uma delas mexe no valor da fatura inteira.
   */
  hasResolvedContestation?: boolean;
}

/**
 * A medição pode receber contestação. O serviço é quem decide de fato — isto evita oferecer uma
 * ação que voltaria com `409`, e num ponto vai além dele de propósito.
 *
 * A medição passa por duas fases contestáveis, com um intervalo fechado entre elas:
 *
 * 1. **publicada, aguardando o gerador** — contestável enquanto a janela de retenção estiver
 *    aberta (`release_at`), que é o prazo que o cliente tem para discordar antes do faturamento;
 * 2. **liberada e ainda sem OS** — a janela acabou e a fatura ainda não existe: não há o que
 *    contestar, nem prazo de cliente correndo. É o intervalo;
 * 3. **faturada** — a fatura existe e volta a ser contestável, agora até o fim dos 120 dias
 *    corridos da publicação (RN-01); a correção vira crédito no ciclo seguinte (RN-27).
 *
 * **Contestação já resolvida encerra a fase 1.** Quem contestou teve resposta — aceita ou
 * rejeitada — e reabrir sobre a mesma medição antes de ela virar fatura repetiria um rito que
 * acabou de terminar. A RN-03 do serviço só barra contestação **ativa**, então esta parte é mais
 * restritiva que o backend, por decisão de produto.
 *
 * `CONTESTED` continua contestável: com uma contestação em curso, outro objeto do mesmo ciclo
 * ainda pode ser aberto (RN-40/S19).
 */
export function canContest(window: ContestationWindow, now: Date = new Date()): boolean {
  const { releaseStatus, publishedAt, releaseAt, isInvoiced, hasResolvedContestation } = window;

  if (releaseStatus == null || releaseStatus === 'CANCELLED') return false;

  const deadline = contestationDeadline(publishedAt);
  if (!deadline || isAfter(now, deadline)) return false;

  if (isInvoiced) return true;
  if (releaseStatus === 'CONTESTED') return true;
  if (hasResolvedContestation) return false;
  if (releaseStatus === 'READY_TO_INVOICE') return false;

  return !retentionWindowClosed(releaseAt, now);
}

export interface RowContestationDeadline {
  releaseStatus: ReleaseStatusLike;
  releaseAt?: string | null;
  isInvoiced: boolean;
}

/**
 * Prazo a exibir numa listagem (extrato) — só para medição, nunca para fatura.
 *
 * **Decisão de produto:** uma fatura já é contestável por até 120 dias da publicação
 * (`canContest` conhece essa regra), mas mostrar esse prazo na linha de uma fatura já emitida
 * evidencia uma possibilidade que não queremos convidar o gerador a exercer — o extrato não deve
 * empurrar contestação de algo já faturado. O prazo só aparece enquanto o ciclo ainda é medição
 * (não tem OS na Omie): é a janela normal de revisão antes de virar fatura.
 *
 * Também **sem** `hasResolvedContestation` (gap não-bloqueante do handoff): numa tabela
 * paginada, saber se a contestação já foi decidida exigiria uma chamada REST por linha ao
 * contestacao-service. Efeito aceito: uma medição `AWAITING_GENERATOR` com contestação já
 * resolvida pode aparecer aqui com um prazo que a tela de detalhe recusaria.
 */
export function contestationDisplayDeadline(
  row: RowContestationDeadline,
  now: Date = new Date(),
): Date | null {
  const { releaseStatus, releaseAt, isInvoiced } = row;

  // Já é fatura: nunca mostra, mesmo estando dentro dos 120 dias — ver decisão de produto acima.
  if (isInvoiced) return null;

  if (releaseStatus == null || releaseStatus === 'CANCELLED') return null;

  // Janela fechou, fatura ainda não existe — intervalo sem nada contestável (fase 2).
  if (releaseStatus === 'READY_TO_INVOICE') return null;

  // Fase 1: aguardando o gerador — o prazo é o fim da janela de retenção, não os 120 dias.
  if (releaseStatus === 'AWAITING_GENERATOR') {
    if (!releaseAt) return null;
    const releaseDate = parseISO(releaseAt);
    if (!isValid(releaseDate) || isAfter(now, releaseDate)) return null;
    return releaseDate;
  }

  // CONTESTED sem fatura: o prazo continua sendo o da revisão da medição — mesma data que
  // `retentionWindowDeadline` mostra no detalhe.
  if (releaseStatus === 'CONTESTED' && releaseAt) {
    const releaseDate = parseISO(releaseAt);
    if (!isValid(releaseDate) || isAfter(now, releaseDate)) return null;
    return releaseDate;
  }

  // Sem janela de retenção não há data a exibir. Aqui se caía no teto dos 120 dias, que saiu de
  // tela em 08/09/2026 — continua valendo como regra em `canContest`, só não é mais exibido.
  return null;
}
